import { Slot } from "@radix-ui/react-slot"
import { type VariantProps, cva } from "class-variance-authority"
import type * as React from "react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "gb-faux-border [--faux-surface:var(--background)] shadow-xs hover:[--faux-surface:var(--accent)] hover:text-accent-foreground focus-visible:[--faux-border:var(--ring)] aria-invalid:[--faux-border:var(--destructive)]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        // Brand: "ink tile" — dark green tile stamped into paper.
        // Depth grammar: wall shadow beneath, inset top highlight + bottom
        // lip, soft cast shadow. Hover lifts 1px; press collapses the wall.
        ink: "gb-tile gb-tile--ink font-sans",
        // Brand: "paper tile" — cream ghost sibling to `ink`. Same grammar.
        paper: "gb-tile gb-tile--paper font-sans",
        // Brand: "lime tile" — Lime-50 sibling for dark (Dark Lime)
        // surfaces, where ink would vanish. Same grammar. The default
        // ring (--ring/50 = lime-70 at half alpha) is invisible against the
        // lime face and under 3:1 on the Dark Lime ground, so the focus ring
        // goes white here.
        lime: "gb-tile gb-tile--lime font-sans focus-visible:ring-white/80",
        // Brand: outlined Lime secondary for dark surfaces — the quiet
        // sibling to `lime`. Flat by design; depth stays on primary tiles.
        "lime-outline":
          "border border-lime-50 text-lime-50 bg-transparent hover:bg-lime-50/10 focus-visible:ring-lime-50/40",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        // lg and xl take text-link (15px): at 44px+ these are the marketing
        // CTAs that sit beside text-link ArrowLinks, and matching label
        // metrics keeps the shared baseline across a CTA row.
        lg: "text-link h-11 rounded-md px-6 has-[>svg]:px-4",
        // On phones xl steps down to the 44px touch floor so the CTA stays
        // proportionate to a narrow viewport.
        xl: "text-link h-14 rounded-[10px] [--faux-radius:10px] px-7 has-[>svg]:px-6 max-sm:h-11 max-sm:px-6 max-sm:text-sm",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    // Tile variants use a consistent 4px radius across all sizes — the
    // paper-kit spec. Listed here so this wins over size-level overrides.
    compoundVariants: [
      { variant: ["ink", "paper", "lime"], className: "rounded-[4px]" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Button, buttonVariants }
